import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Security_UserSalesChannelAssociation,
  Security_UserSalesChannelInventoryStoreAssociation
} from 'invApi';
import { createServicesThunk } from '../api/thunk.helper';
import { getSelectedSystemId } from '../appSettings/app.settings.state';
import { getCodeTableDictionary } from '../codeTables/codeTables.state';
import {
  DEFAULT_USER_DISPLAY_LANGUAGE,
  DEFAULT_USER_FORCE_LANGUAGE_OVERRIDE,
  DEFAULT_USER_LANGUAGE
} from '../constants/common';
import { DEFAULT_RESULTS_PER_PAGE } from '../constants/paging';
import { transformKeyValueArrayToObject } from '../helpers/array.helpers';
import {
  BusinessUnit,
  CoreState,
  Group,
  KeyValuePair,
  RetrieveSessionResponse,
  SecurityAttributeSetting,
  SessionId,
  SessionState
} from '../interfaces';
import { CodeTableNumber } from '../interfaces/codeTables/codeTables';
import {
  getUserDisplayLanguagePreferenceKey,
  getUserFeatureToggleOverridesPreferenceKey,
  getUserForceHeaderLanguageOverridePreferenceKey,
  getUserInventoryStorePreferenceKey,
  getUserLanguagePreferenceKey,
  getUserSalesChannelPreferenceKey
} from '../session/session.helpers';
import { FeatureToggleDataType } from '../featureToggle/featureToggle.constants';

/**
 * Retrieve information about the current session.  This call requires that the current sessionID is in sessionStorage
 */
export const retrieveSession = createServicesThunk<
  undefined,
  RetrieveSessionResponse
>({
  path: 'security/retrieveSession'
});

/**
 * Logout. Invalidates the session token
 */
export const closeSession = createServicesThunk<undefined, null>({
  path: 'security/closeSession'
});

/**
 * Retrieve a new sessionId
 */
export const cloneUserSession = createServicesThunk<
  undefined,
  RetrieveSessionResponse
>({
  path: 'security/cloneUserSession'
});

interface UpdateSessionBusinessUnitRequest {
  NewBusinessUnitSystemId: string;
}
/**
 * Update the active business unit
 */
export const updateSessionBusinessUnit = createServicesThunk<
  UpdateSessionBusinessUnitRequest,
  RetrieveSessionResponse
>({
  path: 'security/updateSessionBusinessUnit'
});

interface UpdatePreferencesRequest {
  Preferences: Array<KeyValuePair<string>>;
}

/** Update the user preferences */
export const updatePreferences = createServicesThunk<
  UpdatePreferencesRequest,
  RetrieveSessionResponse
>({
  path: 'security/updatePreferences'
});

interface UpdateCredentialsRequest {
  CurrentPassword: string;
  NewPassword: string;
}
/** Update the current users password */
export const updateCredentials = createServicesThunk<
  UpdateCredentialsRequest,
  {}
>({
  path: 'security/UpdateCredentials'
});

// Reducers
const initialState: SessionState = {
  data: null,
  id: null,
  retrieveSession: {
    loadError: null
  }
};

const slice = createSlice({
  name: 'session',
  initialState,
  reducers: {
    setSessionId: (state, action: PayloadAction<SessionId>) => {
      state.id = action.payload;
    }
  },
  //https://redux-toolkit.js.org/usage/usage-with-typescript/#type-safety-with-extrareducers
  extraReducers: (builder) => {
    builder
      .addCase(retrieveSession.fulfilled, (state, action) => {
        state.data = action.payload;
        state.id = action.payload.SessionId;
      })
      .addCase(retrieveSession.rejected, (state, action) => {
        // session expired
        state.retrieveSession.loadError = action.payload;
        if (action.payload) {
          state.id = null;
        }
      })
      .addCase(closeSession.fulfilled, (state) => {
        state.id = null;
        state.data = null;
      })
      .addCase(closeSession.rejected, (state) => {
        state.id = null;
        state.data = null;
      })
      .addCase(updateSessionBusinessUnit.fulfilled, (state, action) => {
        state.id = action.payload.SessionId;
      });
  }
});

export default slice.reducer;

// Actions
export const { setSessionId } = slice.actions;

// Selectors
const getSession = (state: CoreState) => state.core.session;

export const getSessionDetails = createSelector(
  [getSession],
  (session) => session.data
);

export const getSessionId = createSelector(
  [getSession],
  (session) => session.id
);

export const getSessionSummary = createSelector(
  [getSessionDetails],
  (sessionDetail) => sessionDetail?.SessionSummary
);

const EMPTY_BUSINESS_UNITS: Array<BusinessUnit> = [];
export const getBusinessUnits = createSelector(
  [getSessionSummary],
  (sessionSummary) => sessionSummary?.BusinessUnits || EMPTY_BUSINESS_UNITS
);

const EMPTY_SECURITY_ATTRIBUTES: Array<SecurityAttributeSetting> = [];
export const getSecurityAttributes = createSelector(
  [getSessionSummary],
  (sessionSummary) =>
    sessionSummary?.AssignedSecurityAttributes || EMPTY_SECURITY_ATTRIBUTES
);

const EMPTY_ROLES: Array<number> = [];
export const UserSecurityRolesSelector = createSelector(
  [getSessionSummary],
  (sessionSummary) => sessionSummary?.Roles || EMPTY_ROLES
);

const EMPTY_SALES_CHANNELS: Array<Security_UserSalesChannelAssociation> = [];
export const UserSalesChannelsSelector = createSelector(
  [getSessionSummary],
  (sessionSummary) => sessionSummary?.SalesChannels || EMPTY_SALES_CHANNELS
);

export const getSelectedBusinessUnit = createSelector(
  [getBusinessUnits, getSelectedSystemId],
  (businessUnits, systemId) => {
    const selectedBu = businessUnits.find((bu) => {
      return bu.SystemId === systemId;
    });

    if (!selectedBu) {
      throw new Error('Business unit does not exist');
    }

    return selectedBu;
  }
);

export const getPreferences = createSelector(
  [getSessionSummary],
  (sessionSummary) => {
    return transformKeyValueArrayToObject(sessionSummary?.Preferences);
  }
);

export const getSelectedBusinessUnitDefaultLanguage = createSelector(
  [getBusinessUnits, getSelectedSystemId],
  (businessUnits, systemId) => {
    const selectedBu = businessUnits.find((bu) => {
      return bu.SystemId === systemId;
    });

    return selectedBu?.DefaultLanguage;
  }
);

export const UserDisplayLanguagePreferenceSelector = createSelector(
  [getPreferences, getSelectedSystemId, getSelectedBusinessUnitDefaultLanguage],
  (preferences, systemId, selectedBusinessUnitDefaultLanguage) => {
    return (
      preferences[getUserDisplayLanguagePreferenceKey(systemId)] ||
      selectedBusinessUnitDefaultLanguage ||
      DEFAULT_USER_DISPLAY_LANGUAGE
    );
  }
);

export const userIdSelector = createSelector(
  [getSessionSummary],
  (sessionSummary) => sessionSummary?.Id
);

export const UserLanguagePreferenceSelector = createSelector(
  [getPreferences, getSelectedSystemId, getSelectedBusinessUnitDefaultLanguage],
  (preferences, systemId, selectedBusinessUnitDefaultLanguage) => {
    return (
      preferences[getUserLanguagePreferenceKey(systemId)] ||
      selectedBusinessUnitDefaultLanguage ||
      DEFAULT_USER_LANGUAGE
    );
  }
);

export const GetPreferredSalesChannelSelector = createSelector(
  [getPreferences, getSelectedSystemId, UserSalesChannelsSelector],
  (preferences, systemId, userSalesChannels) => {
    /* Check if the sales channel in preferences is Active and associated with the user */
    return userSalesChannels?.find(
      (channel) =>
        channel?.Active &&
        channel?.SalesChannelId ===
          +preferences[getUserSalesChannelPreferenceKey(systemId)]
    );
  }
);

export const UserDefaultSalesChannelIdSelector = createSelector(
  [UserSalesChannelsSelector, GetPreferredSalesChannelSelector],
  (userSalesChannels, preferredSalesChannel) => {
    return preferredSalesChannel
      ? preferredSalesChannel?.SalesChannelId
      : userSalesChannels?.find(
          (channel: Security_UserSalesChannelAssociation) =>
            channel?.Active && channel?.Default
        )?.SalesChannelId;
  }
);

export const UserDefaultInventoryStoreIdSelector = createSelector(
  [
    getPreferences,
    getSelectedSystemId,
    UserSalesChannelsSelector,
    GetPreferredSalesChannelSelector,
    UserDefaultSalesChannelIdSelector
  ],
  (
    preferences,
    systemId,
    userSalesChannels,
    preferredSalesChannel,
    userDefaultSalesChannelId
  ) => {
    const userSalesChannelInventoryStores =
      (userDefaultSalesChannelId &&
        userSalesChannels?.find(
          (channel: Security_UserSalesChannelAssociation) =>
            channel?.Active &&
            channel?.SalesChannelId === userDefaultSalesChannelId
        )?.InventoryStores) ||
      [];

    const isValidPreferredInventoryStore = (
      preferredSalesChannel?.InventoryStores || []
    )?.find(
      (store: Security_UserSalesChannelInventoryStoreAssociation) =>
        store?.InventoryStoreId ===
        +preferences[getUserInventoryStorePreferenceKey(systemId)]
    );

    /* 
      If the inventory store in preferences is not associated with a preferred sales channel, 
      use the default inventory store from default sales channel
    */
    return isValidPreferredInventoryStore
      ? isValidPreferredInventoryStore?.InventoryStoreId
      : userSalesChannelInventoryStores?.find(
          (store: Security_UserSalesChannelInventoryStoreAssociation) =>
            store?.Default
        )?.InventoryStoreId;
  }
);

export const UserForceLanguageOverrideSelector = createSelector(
  [getPreferences, getSelectedSystemId],
  (preferences, systemId) => {
    return (
      preferences[getUserForceHeaderLanguageOverridePreferenceKey(systemId)] !==
      DEFAULT_USER_FORCE_LANGUAGE_OVERRIDE
    );
  }
);

export const UserPageSizeSelector = createSelector(
  [getPreferences],
  (preferences) => {
    return +preferences?.PageSize || DEFAULT_RESULTS_PER_PAGE;
  }
);

const EMPTY_GROUPS: Group[] = [];
export const UserGroupsSelector = createSelector(
  [getSessionSummary],
  (sessionSummary) => {
    return sessionSummary?.Groups || EMPTY_GROUPS;
  }
);

export const ActiveUserGroupsSelector = createSelector(
  [UserGroupsSelector],
  (userGroups) => {
    return userGroups.filter((userGroup) => {
      return userGroup.Active;
    });
  }
);

export const UserEmailSelector = createSelector(
  [getSessionSummary],
  (sessionSummary) => {
    return sessionSummary?.Email;
  }
);

export const IsUserGlobalSelector = createSelector(
  [getSessionSummary],
  (sessionSummary) => {
    return sessionSummary?.Global || false;
  }
);

export const ActiveUserGroupIdsSelector = createSelector(
  [ActiveUserGroupsSelector],
  (userGroups) => {
    return userGroups.map((userGroup) => {
      return userGroup.Id;
    });
  }
);

export const BusinessUnitTimeZoneSelector = createSelector(
  [getSelectedBusinessUnit, getCodeTableDictionary(CodeTableNumber.TimeZone)],
  (businessUnit, timeZones) => {
    return Object.values(timeZones || {}).find((timeZone) => {
      return +timeZone.Value === businessUnit.TimeZoneCode;
    });
  }
);

export const UserFeatureToggleOverridesSelector = createSelector(
  [getPreferences, getSelectedSystemId],
  (
    preferences,
    systemId
  ): Partial<Record<FeatureToggleDataType, boolean>> | null => {
    const raw =
      preferences[getUserFeatureToggleOverridesPreferenceKey(systemId)];

    if (!raw) return null;

    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
);
